import type { ComponentType } from 'react'
import React, { useCallback, forwardRef, useState, useEffect } from 'react'
import { withDependencies, optional } from '@wix/thunderbolt-ioc'
import { useService } from '@wix/services-manager-react'
import { ContextProviderRegistrySymbol, FunctionLibraryRegistrySymbol } from './symbols'
import type { ContextProviderRegistry, FunctionLibraryRegistry } from './types'
import { ContextBindingServiceDefinition } from './services'
import { ExperimentsSymbol, ViewerModelSym } from '@wix/thunderbolt-symbols'
import type { Experiments, ViewerModel } from '@wix/thunderbolt-symbols'
import { setDeep, getBoundProps } from './contextBindingUtils'

export const ContextBindingWrapper = withDependencies(
	[ContextProviderRegistrySymbol, FunctionLibraryRegistrySymbol, ExperimentsSymbol, optional(ViewerModelSym)],
	(
		contextProviderRegistry: ContextProviderRegistry,
		functionLibraryRegistry: FunctionLibraryRegistry,
		experiments: Experiments,
		viewerModel: ViewerModel
	) => {
		const isDebug = viewerModel?.mode?.debug
		const servicesInfraExperiment = Boolean(experiments['specs.thunderbolt.servicesInfra'])
		const contextProvidersEnabled = Boolean(experiments['specs.thunderbolt.contextProviders'])

		const shouldWrap = servicesInfraExperiment && contextProvidersEnabled

		if (!shouldWrap) {
			return {
				wrapComponent(Component: ComponentType<any>) {
					return Component
				},
			}
		}

		return {
			wrapComponent: (Component: ComponentType<any>) => {
				return forwardRef<any, any>((props, ref) => {
					const contextBindingService = useService(ContextBindingServiceDefinition)
					const [, setTick] = useState(0)
					const forceUpdate = useCallback(() => setTick((tick) => tick + 1), [])

					// subscribe to binding changes on mount and unsub on unmount
					useEffect(() => {
						return contextBindingService.subscribeToBindingChanges(props.compId, forceUpdate)
					}, [props.compId, forceUpdate, contextBindingService])

					const bindings = contextBindingService.getBindings(props.compId)
					const boundProps = getBoundProps(bindings, contextProviderRegistry, functionLibraryRegistry, isDebug)

					const innerElementsBindings = contextBindingService.getInnerElementsBindings(props.compId)
					const boundInnerElementProps: Record<string, unknown> = innerElementsBindings.reduce<Record<string, unknown>>(
						(agg, { path, bindings }) => {
							const propsOnPath = getBoundProps(bindings, contextProviderRegistry, functionLibraryRegistry, isDebug)
							return setDeep(agg, path, propsOnPath)
						},
						props.elementProps || {}
					)

					return <Component {...props} {...boundProps} elementProps={boundInnerElementProps} ref={ref} />
				})
			},
		}
	}
)
